import { CollectionPlaceholder } from "@assets/index";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Image as CImage,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import LoadingAnimation from "@components/Loader/LoadingAnimation";
import { NextImage } from "@components/NextImage/NextImage";
import CheckIcon from "@icons/CheckIcon";
import { NFTEvent, NFTEventTier } from "@models/event";
import { NFTCollection, NFTCollectionParam, toNFTId } from "@models/nft";
import { Transaction } from "@models/payment";
import { User } from "@models/user";
import { walletAddressPreview } from "@models/utils";
import { getCollectionsByEventId } from "@providers/checkout/collection";
import {
  checkTierEligibility,
  getEventEligibility,
} from "@providers/checkout/event";
import { useQuery } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";

const _t = (key?: string) => key || "";

const AccordionItemWrapper = ({
  tier,
  collections,
  whitelists,
}: {
  tier: NFTEventTier;
  collections: Array<NFTCollection & { eligible: boolean }>;
  whitelists: Transaction[];
}) => {
  const titleTextColor = useColorModeValue("text-black", "text-white");
  const subTitleTextColor = useColorModeValue("text-black", "text-secondary");

  return (
    <AccordionItem
      borderTop={0}
      borderBottom="1px"
      borderColor="gray.200"
      className="last:border-none"
    >
      <AccordionButton px={2} py={5}>
        <div className="flex flex-row flex-grow items-center">
          {tier.imageUrl ? (
            <CImage w={14} src={tier.imageUrl} alt="" borderRadius={4} />
          ) : (
            <NextImage
              className="w-14 rounded"
              src={CollectionPlaceholder}
              alt=""
            />
          )}
          <div className="flex flex-col font-medium text-base flex-grow text-left">
            <p className={titleTextColor}>{tier.name}</p>
            {/* show claimed quantity here */}
            {/* {whitelists && whitelists.length > 0 && ( */}
            {/*   <p className="text-sm text-primary mt-1">{`Claimed Quantity: ${whitelists.reduce( */}
            {/*     (acc, curr) => acc + curr.items.length, */}
            {/*     0 */}
            {/*   )}`}</p> */}
            {/* )} */}
          </div>
        </div>
        <AccordionIcon width="2rem" fontSize="2rem" color="neutral.300" />
      </AccordionButton>
      <AccordionPanel>
        <div className={"flex flex-col text-sm " + subTitleTextColor}>
          {!isEmpty(collections) && (
            <>
              {collections.map((collection, i) => (
                <div
                  key={i}
                  className="flex flex-row flex-grow items-center gap-4"
                >
                  {collection?.imageUrl ? (
                    <CImage
                      w={14}
                      src={collection.imageUrl}
                      alt=""
                      borderRadius={4}
                    />
                  ) : (
                    <NextImage
                      className="w-14 rounded"
                      src={CollectionPlaceholder}
                      alt=""
                    />
                  )}
                  <div className="flex flex-col font-medium text-base flex-grow text-left">
                    <p
                      className={`text-md font-medium text-[#23262F] ${titleTextColor}`}
                    >
                      {/* TODO: add attribute */}
                      {collection.collectionName}
                    </p>
                    <p className="text-neutral-300 text-xs">
                      {walletAddressPreview(collection?.collectionId || "")}
                    </p>
                  </div>
                  <div className="mr-2">
                    {collection.eligible ? (
                      <div className="flex flex-row items-center text-primary-500 gap-4">
                        <p className="font-medium hidden @4xl:block">
                          {_t("You're Eligible")}
                        </p>
                        <Icon as={CheckIcon} boxSize={7} />
                      </div>
                    ) : (
                      <>{/* <Button>Get</Button> */}</>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

const TierEligibility = ({
  tiersWithCollectionInfos,
  tierWhitelists,
  user,
}: {
  tiersWithCollectionInfos: {
    tier: NFTEventTier;
    collections: Array<NFTCollection & { eligible: boolean }>;
  }[];
  tierWhitelists: {
    tier: NFTEventTier;
    whitelists: Transaction[];
  }[];
  user: User | null;
}) => {
  const router = useRouter();
  const titleTextColor = useColorModeValue("text-black", "text-white");
  const subTitleTextColor = useColorModeValue("text-black", "text-secondary");
  const accordionBgColor = useColorModeValue("white", "");

  if (isEmpty(tiersWithCollectionInfos)) {
    return <></>;
  }

  return (
    <div className="flex flex-col justify-center w-full px-0 mb-16">
      <p className={"text-2xl mb-4 " + titleTextColor}>Eligibility</p>
      <p className={"text-base py-2 " + subTitleTextColor}>
        {user ? (
          <>You have to be a owner of:</>
        ) : (
          <>
            Make sure to&nbsp;
            <a href={`/auth/${encodeURIComponent(router.asPath)}`}>
              <b className="text-primary underline">{"log in"}</b>
            </a>
            &nbsp;with the crypto wallet holding the appropriate NFT.
          </>
        )}
      </p>
      <Accordion bg={accordionBgColor} allowMultiple>
        {tiersWithCollectionInfos.map(({ tier, collections }, i) => (
          <AccordionItemWrapper
            key={i}
            tier={tier}
            collections={collections}
            whitelists={
              tierWhitelists.find((t) => t.tier.id === tier.id)?.whitelists ||
              []
            }
          />
        ))}
      </Accordion>
    </div>
  );
};

const EventEligibility = ({
  collections,
  user,
}: {
  collections: Array<NFTCollectionParam & { eligible: boolean }>;
  user: User | null;
}) => {
  const router = useRouter();
  const titleTextColor = useColorModeValue("text-[#23262F]", "text-white");
  const subTitleTextColor = useColorModeValue(
    "text-[#444444]",
    "text-[#DEDEE0]"
  );
  console.log("collections", collections);

  return (
    <div className="flex flex-col justify-center w-full px-0 mb-16">
      <p className={"text-2xl mb-4 " + titleTextColor}>Eligibility</p>
      <p className={"text-base py-2 mb-2 " + subTitleTextColor}>
        {user ? (
          <>You have to be an owner of:</>
        ) : (
          <>
            Make sure to&nbsp;
            <a href={`/auth/${encodeURIComponent(router.asPath)}`}>
              <b className="text-primary underline">{"log in"}</b>
            </a>
            &nbsp;with the crypto wallet holding the appropriate NFT(s).
          </>
        )}
      </p>

      {collections.map((collection, i) => (
        <div
          key={i}
          className="flex flex-row flex-grow items-center gap-4 backdrop-blur-sm bg-white/10 shadow mb-3 p-3 rounded-md"
        >
          {collection?.imageUrl ? (
            <CImage w={14} src={collection.imageUrl} alt="" borderRadius={4} />
          ) : (
            <NextImage
              className="w-14 rounded"
              src={CollectionPlaceholder}
              alt=""
            />
          )}
          <div className="flex flex-col font-medium text-base flex-grow text-left">
            <p
              className={`text-md font-medium text-[#23262F] ${titleTextColor}`}
            >
              {/* TODO: add attribute */}
              {collection.collectionName}
            </p>
            <p className="text-neutral-300 text-xs">
              {walletAddressPreview(collection?.collectionId || "")}
            </p>
          </div>
          <div className="mr-2">
            {collection.eligible ? (
              <div className="flex flex-row items-center text-primary-500 gap-4">
                <p className="font-medium hidden @4xl:block">
                  {_t("You're Eligible")}
                </p>
                <Icon as={CheckIcon} boxSize={7} />
              </div>
            ) : (
              <>{/* <Button>Get</Button> */}</>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const EventDetailEligibilityWrapper = ({
  event,
  user,
}: {
  event: NFTEvent;
  user: User | null;
}) => {
  const { isLoading, data: eligibilityData } = useQuery(
    [`eligibility:${event?.id}`],
    () => getEventEligibility(event!.id, user!.id),
    { enabled: !!user && !!event }
  );

  const [tiers, setTiers] = useState<NFTEventTier[] | null>(null);
  const [whitelists, setWhitelists] = useState<Transaction[] | null>(null);
  const [tiersWithCollectionInfos, setTiersWithCollectionInfos] = useState<
    | {
        tier: NFTEventTier;
        collections: Array<NFTCollectionParam & { eligible: boolean }>;
      }[]
    | null
  >(null);
  const [collections, setCollections] = useState<
    Array<NFTCollectionParam & { eligible: boolean }>
  >([]);

  useEffect(() => {
    if (!eligibilityData || !event) return;
    setWhitelists(eligibilityData.whitelists);

    for (let i = 0; i < event.tiers.length; i++) {
      const tier = event.tiers[i];
      const tierNFTs = (eligibilityData.nfts || []).filter(
        (n) =>
          tier.nftConstraint?.collections.find(
            (c) => c.network == n.network && c.collectionId == n.collectionId
          )
      );
      const tierWhitelists = (eligibilityData.whitelists || []).filter(
        (r) => r.tierId == tier.id
      );
      const tierMoonpasses = (eligibilityData.moonpasses || []).filter(
        (moonpass) =>
          tier.moonpassConstraint
            ? tier.moonpassConstraint.edition == "all" ||
              tier.moonpassConstraint.edition == moonpass.edition
            : false
      );

      event.tiers[i].eligible = checkTierEligibility(
        tier,
        tierNFTs,
        tierWhitelists,
        tierMoonpasses
      );
    }
    setTiers(event.tiers);
  }, [event, eligibilityData]);

  useEffect(() => {
    if (!event) return;
    setTiers(event.tiers);
  }, [event]);

  useEffect(() => {
    if (!event) return;
    const nftIds = (eligibilityData?.nfts || []).map((n) =>
      toNFTId([n.network, n.collectionId])
    );
    const getEventCollections = async () => {
      const collections = await getCollectionsByEventId(event.id);
      const collectionsWithEligibility = collections.map((collection) => ({
        ...collection,
        eligible: nftIds.includes(collection.id),
      }));
      setCollections(collectionsWithEligibility);
    };
    getEventCollections();
  }, [event, eligibilityData?.nfts]);

  useEffect(() => {
    if (!tiers || !event || !eligibilityData) return;
    const getEventCollections = async () => {
      const collections = await getCollectionsByEventId(event.id);
      const nftIds = eligibilityData.nfts.map((n) =>
        toNFTId([n.network, n.collectionId])
      );
      const tiersWithCollectionInfos = tiers.map((tier: NFTEventTier) => {
        const tierCollections = collections
          .filter(
            (collection) =>
              tier.nftConstraint?.collections.find((c) => c.id == collection.id)
          )
          .map((collection) => ({
            ...collection,
            eligible: nftIds.includes(collection.id), //TODO: check nft attributes
          }));
        return { tier, collections: tierCollections };
      });
      setTiersWithCollectionInfos(tiersWithCollectionInfos);
    };
    getEventCollections();
  }, [tiers, event, eligibilityData]);

  const tierWhitelists: { tier: NFTEventTier; whitelists: Transaction[] }[] =
    useMemo(() => {
      if (!tiers || !whitelists) return [];
      return tiers.map((tier: NFTEventTier) => ({
        tier,
        whitelists: whitelists.filter(
          (whitelist: Transaction) => whitelist.tierId == tier.id
        ),
      }));
    }, [tiers, whitelists]);

  if (user && isLoading)
    return (
      <div className="flex flex-col justify-center w-full px-0 mb-16">
        <LoadingAnimation />
      </div>
    );

  if (!isEmpty(collections)) {
    return <EventEligibility user={user} collections={collections} />;
  }

  // in case of showing eligibility for different tiers
  /* if (tiersWithCollectionInfos) */
  /*   return ( */
  /*     <TierEligibility */
  /*       tiersWithCollectionInfos={tiersWithCollectionInfos} */
  /*       tierWhitelists={tierWhitelists} */
  /*       user={user} */
  /*     /> */
  /*   ); */

  return <></>;
};

export default EventDetailEligibilityWrapper;
